import React, { useEffect, useMemo, useState } from "react";
import Divider from "antd/es/divider";
import Drawer from "antd/es/drawer";
import Layout from "antd/es/layout";
import styled, { useTheme } from "styled-components";
import AppstoreFilled from "@ant-design/icons/AppstoreFilled";
import CaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import CrownOutlined from "@ant-design/icons/CrownOutlined";
import EyeOutlined from "@ant-design/icons/EyeOutlined";
import FundProjectionScreenOutlined from "@ant-design/icons/FundProjectionScreenOutlined";
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import PieChartOutlined from "@ant-design/icons/PieChartOutlined";
import QuestionCircleOutlined from "@ant-design/icons/QuestionCircleOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import ShopOutlined from "@ant-design/icons/ShopOutlined";
import SwapOutlined from "@ant-design/icons/SwapOutlined";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import {
  AFFILIATION_PATH,
  BASE_BILLING_SETTINGS_PATH,
  COLLAPSED_DESKTOP_MENU,
  CREATE_TRADE_PATH,
  DASHBOARD_PATH,
  DCA_PUBLIC_BOTS_PATH,
  GROUP_MANAGEMENT_PATH,
  MARKETPLACE_PATH,
  MOBILE_MENU,
  NOTIFICATIONS_SETTINGS_PATH,
  OPEN_TRADES_PATH,
  PATH_TO_FEATURE_FLAG,
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_ASSETS_PATH,
  SECURITY_SETTINGS_PATH,
  SETTINGS_PATH,
  USER_SIGNALS_PATH,
  WATCHLIST_PATH,
  MY_GROUPS_PATH,
  ADMIN_SIGNALS_PATH,
  ADMIN_TRADING_CONFIGURATION_PATH,
  BASE_TRADES_PATH,
  IS_TELEGRAM_WEB_APP,
  IS_WEB_VIEW,
} from "common/constants";
import useMedia from "utils/hooks/useMedia";
import { css } from "styled-components";
import { selectIsDarkMode } from "@shared/slices/common.slice";
import SocialMedia from "common/Sider/SocialMedia";
import Menu from "common/SideMenu/Menu";
import {
  useBottomNavigator,
  useIsOnboarding,
  useTelegramSafeAreaInsets,
} from "utils/hooks/misc";
import { zIndexes } from "@shared/common/Themes/AppTheme";
import { MenuItemTextStyled } from "common/Home/Menu/MenuItemText";
import Button from "common/Buttons/Button";
import localStorageService from "utils/localStorageService";
import { hexToRGB } from "@shared/utils/misc";
import WorkspaceSelector from "common/Sider/WorkspaceSelector";
import { MenuItemConfig, SiderGroup, WorkspaceToPathsMapping } from "types";
import useWorkspace from "utils/hooks/useWorkspace";
import { getGroupManagementRoute } from "utils/routes";
import { getWorkspaceToPath } from "common/Sider/utils";
import {
  AccountsIcon,
  BillingIcon,
  DashboardIcon,
  GroupManagementIcon,
  GroupsIcon,
  SignalsIcon,
  Sliders,
} from "common/Icons/Icons";
import QrPopover from "common/Sider/QrPopover";
import {
  BOT_TYPE_TO_BASE_BACKTESTING_PATH,
  BOT_TYPE_TO_BASE_MY_BOTS_PATH,
  BOT_TYPE_TO_BASE_PATH,
} from "features/bots/common/constants";
import {
  BOT_TYPE_TO_DISPLAY_NAME,
  BOT_TYPES,
} from "@shared/features/bots/constants";
import { BotType, Inset, WorkspaceType } from "@shared/types";
import Flex from "common/Boxes/Flex";
import {
  BOT_TYPE_TO_ICON,
  BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON,
  DARK_BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON,
} from "features/settings/common/constants";
import AdminCommunityBanner from "common/Sider/AdminCommunityBanner";
import UpgradeButton from "common/Home/Header/UpgradeButton";
import { sharedLib } from "@shared/services/shared.service";
import { WORKSPACE_TO_DISPLAY_NAME } from "@shared/features/workspaces/constants";
import { HEADER_HEIGHT } from "common/Home/constants";
import WorkspaceSwitch from "common/Home/Header/WorkspaceSwitch";
import GroupSelect from "features/admin/group/GroupSelect";
import CurrencyToggle from "common/Sider/CurrencyToggle";
import UserAvatar from "common/Avatars/UserAvatar";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { useUserInfo } from "@shared/utils/hooks/misc";
import useLogout from "utils/hooks/useLogout";
import DarkModeToggle from "common/Home/Header/DarkModeToggle.tsx";
import { isAndroid, isIOS } from "react-device-detect";
import { showIntercomSpace } from "utils/misc.tsx";

interface SiderProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (isOpen: boolean) => void;
}

const Sider = ({ mobileMenuOpen, setMobileMenuOpen }: SiderProps) => {
  const appTheme = useTheme();
  const isDarkMode = useSelector(selectIsDarkMode);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  let location = useLocation();
  const history = useHistory();
  const [isMenuAnimatingClose, setIsMenuAnimatingClose] = useState(true);
  const { workspace, onWorkspaceChange, isWorkspaceValid, getHomePath } =
    useWorkspace();
  const { userInfoItems } = useUserInfo();
  const [logout] = useLogout();

  const useDrawerMenu =
    useMedia([`(max-width: ${MOBILE_MENU})`], [true], false) ||
    user?.is_onboarding;
  const showUpgradeButton =
    useDrawerMenu &&
    !user?.is_onboarding &&
    (workspace === WorkspaceType.DEMO || !user?.isFree());

  const bottomNavigatorData = useBottomNavigator();
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const isLargeMobile = useMedia(["(max-width: 750px)"], [true], false);
  const MOBILE_SIDER_DRAWER_WIDTH = bottomNavigatorData.shouldDisplay
    ? 300
    : 250;

  const defaultCollapse = useMedia(
    [`(max-width: ${COLLAPSED_DESKTOP_MENU})`],
    [true],
    false
  );
  const localStorageDefault =
    localStorageService.getParsedValue(COLLAPSED_MENU_KEY);
  const [collapsed, setIsCollapsed] = useState(
    localStorageDefault === null || defaultCollapse
      ? defaultCollapse
      : Boolean(localStorageDefault)
  );

  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location]);

  const handleSetIsCollapsed = (isCollapsed: boolean) => {
    setIsCollapsed(isCollapsed);
    localStorageService.setValue(COLLAPSED_MENU_KEY, isCollapsed);
  };

  useEffect(() => {
    if (defaultCollapse) handleSetIsCollapsed(defaultCollapse);
  }, [defaultCollapse]);

  useEffect(() => {
    if (collapsed) {
      setIsMenuAnimatingClose(false);
      setTimeout(() => setIsMenuAnimatingClose(true), 300);
    }
  }, [collapsed]);

  const userIsLimitedAccess = useIsOnboarding() || user?.isLimitedAccess();
  const showWorkspaceSelector = user?.is_admin && (!collapsed || useDrawerMenu);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const showGroupSelect = isAdminWorkspace && useDrawerMenu;

  const getUserInterfaceClassName = () => {
    if (workspace === WorkspaceType.USER || workspace === WorkspaceType.DEMO)
      return "ant-menu-item-selected";
    if (
      !isWorkspaceValid(WorkspaceType.USER) &&
      !isWorkspaceValid(WorkspaceType.DEMO)
    )
      return "menu-item-disabled";
  };

  const getWorkspaceMenuItem = () => {
    let subMenu: MenuItemConfig[] = [];
    if (user?.is_admin) {
      subMenu.push({
        key: "Admin",
        name: WORKSPACE_TO_DISPLAY_NAME[WorkspaceType.ADMIN],
        onClick: () => onWorkspaceChange(WorkspaceType.ADMIN),
        className:
          workspace === WorkspaceType.ADMIN
            ? "ant-menu-item-selected"
            : undefined,
      });
    }
    subMenu.push({
      key: "User",
      name: WORKSPACE_TO_DISPLAY_NAME[WorkspaceType.USER],
      onClick: () => {
        if (workspace === WorkspaceType.ADMIN)
          onWorkspaceChange(WorkspaceType.USER);
      },
      className: getUserInterfaceClassName(),
    });

    return {
      key: "workspace",
      icon: (
        <IconWrapper>
          <Flex $flexDirection="column">
            <AppstoreFilled />
            <MenuDivider style={{ transform: "translate(0, -4px)" }} />
          </Flex>
        </IconWrapper>
      ),
      name: "Workspaces",
      subMenu,
    };
  };

  const helpMenuItem = {
    key: "help",
    icon: <QuestionCircleOutlined />,
    name: "Help",
    subMenu: [
      {
        key: "intercom_links",
        name: "How To Use",
        onClick: () => showIntercomSpace("help"),
      },
      {
        key: "support",
        name: "Support",
        onClick: () => showIntercomSpace("home"),
      },
    ],
    group: SiderGroup.HELP,
  };

  const getMenuItems = () => {
    let menuItems: MenuItemConfig[];
    if (user?.is_admin && workspace === WorkspaceType.ADMIN) {
      menuItems = workspaceToMenuItems[WorkspaceType.ADMIN];
    } else if (userIsLimitedAccess) {
      menuItems = [SETTINGS_MENU_ITEM, AFFILIATION_MENU_ITEM];
    } else {
      menuItems = workspaceToMenuItems[WorkspaceType.USER];
    }
    if (collapsed && !mobileMenuOpen) {
      menuItems = [...menuItems, helpMenuItem];
      if (user?.is_admin && !useDrawerMenu)
        menuItems = [getWorkspaceMenuItem(), ...menuItems];
    }
    if (bottomNavigatorData.shouldDisplay) {
      menuItems = menuItems.filter(
        (item) =>
          (!IGNORE_BOTTOM_NAVIGATOR_KEYS.has(item.key as any) &&
            !BOT_TYPES.includes(item.key as any)) ||
          (workspace !== WorkspaceType.ADMIN &&
            item.key === AppScreen.AFFILIATION)
      );
    }
    if (isAndroid && IS_WEB_VIEW) {
      menuItems = menuItems.filter((item) => item.key !== AppScreen.BILLING);
    }
    return menuItems;
  };

  const checkIfUserHasAccessToPath = (path: string) => {
    const featureFlag = PATH_TO_FEATURE_FLAG[path];
    return featureFlag && (!user || !user?.hasFeatureFlag(featureFlag));
  };

  const testMissingFeatureFlag = (path: string | string[]) =>
    Array.isArray(path)
      ? path.some((p) => checkIfUserHasAccessToPath(p))
      : checkIfUserHasAccessToPath(path);

  const renderMenuItem = (menuItem: MenuItemConfig, isHeader: boolean) => {
    if (menuItem.routerMatch && testMissingFeatureFlag(menuItem.routerMatch))
      return;
    return {
      key: menuItem.key,
      icon: menuItem.icon,
      label: ((collapsed && isMenuAnimatingClose) || !collapsed) && (
        <MenuItemTextStyled
          menuHeader={isHeader}
          collapsed={collapsed}
          clickable
          link={menuItem.link}
          name={menuItem.name}
          subName={menuItem.subName}
          addNewTag={menuItem.isNewFeature}
        />
      ),
      onClick: menuItem.onClick,
      className: menuItem.className,
    };
  };

  const renderSubMenu = (menuItem: MenuItemConfig) => {
    let submenuItems: MenuItemConfig[] = [];
    if (menuItem.subMenu) submenuItems = [...menuItem.subMenu];
    if (collapsed && !useDrawerMenu) {
      submenuItems = [
        {
          key: menuItem.key.concat("-title"),
          name: menuItem.name,
          link: menuItem.link,
        },
        ...submenuItems,
      ];
    }

    return {
      key: menuItem.key,
      icon: menuItem.icon,
      label: ((collapsed && isMenuAnimatingClose) || !collapsed) && (
        <MenuItemTextStyled
          menuHeader
          collapsed={collapsed}
          name={menuItem.name}
          subName={menuItem.subName}
          addNewTag={menuItem.isNewFeature}
        />
      ),
      onTitleClick: () => {
        if (collapsed && !useDrawerMenu && menuItem.link)
          history.push(menuItem.link);
      },
      children: submenuItems.map((subMenuItem) => {
        return renderMenuItem(subMenuItem, false);
      }),
    };
  };

  const menuItems = getMenuItems();
  const renderMenu = () => {
    const groupNameToChildren: { [key in SiderGroup]?: any[] } = {};
    let processedGroup = false;
    return menuItems.reduce((acc, menuItem) => {
      if (
        menuItem.routerMatch &&
        testMissingFeatureFlag(menuItem.routerMatch)
      ) {
        return acc;
      }
      const menuItemData =
        menuItem.subMenu || (collapsed && !useDrawerMenu)
          ? renderSubMenu(menuItem)
          : renderMenuItem(menuItem, true);
      const siderGroup = menuItem.group;
      if (
        siderGroup &&
        (!bottomNavigatorData.shouldDisplay || processedGroup)
      ) {
        if (siderGroup in groupNameToChildren) {
          groupNameToChildren[siderGroup]?.push(menuItemData);
        } else {
          const children = [menuItemData];
          acc.push({
            key: siderGroup,
            label: collapsed ? <MenuDivider /> : siderGroup,
            type: "group",
            children,
          });
          groupNameToChildren[siderGroup] = children;
        }
      } else {
        acc.push(menuItemData);
      }
      if (siderGroup) processedGroup = true;
      return acc;
    }, [] as any);
  };

  const testMenuItem = (menuItem: MenuItemConfig) => {
    const menuItemPaths = Array.isArray(menuItem.routerMatch)
      ? menuItem.routerMatch
      : [menuItem.routerMatch];
    return menuItemPaths.some((path) =>
      matchPath(location.pathname, {
        path: path,
        exact: menuItem.exactMatch ?? false,
        strict: false,
      })
    );
  };

  const getSelectedMenuItemMatchKeyArray = (
    addParentKey?: boolean
  ): string[] => {
    for (let menuItem of menuItems) {
      if (menuItem.subMenu) {
        for (let subMenuItem of menuItem.subMenu) {
          if (testMenuItem(subMenuItem)) {
            const result = [subMenuItem.key];
            if (addParentKey) {
              result.push(menuItem.key);
            }
            return result;
          }
        }
      }
      if (testMenuItem(menuItem)) {
        return [menuItem.key];
      }
    }

    return [];
  };

  const selectedKeys = getSelectedMenuItemMatchKeyArray();
  const defaultOpenKeys = useMemo(
    () => (collapsed ? undefined : getSelectedMenuItemMatchKeyArray(true)),
    [collapsed]
  );
  const MenuWrapper = useDrawerMenu ? StyledDrawer : StyledMenuWrapper;
  const placement =
    bottomNavigatorData.baseShouldDisplay || user?.is_onboarding
      ? "right"
      : "left";

  const menuProps = {
    drawer: {
      push: false,
      placement: placement as any,
      onClose: () => setMobileMenuOpen(false),
      open: mobileMenuOpen,
      styles: {
        wrapper: { width: MOBILE_SIDER_DRAWER_WIDTH },
        body: { padding: 0, background: appTheme.colors.bgColor },
        header: { display: "none" },
      },
    },
    sider: {
      collapsible: !useDrawerMenu,
      collapsed: collapsed,
    },
  };

  const getSiderTopSection = () => {
    if (bottomNavigatorData.baseShouldDisplay) {
      return (
        <MobileTopSectionWrapper>
          {user && (
            <>
              <UserAvatar
                user={user}
                avatarSize={60}
                fallbackInitialsSize={2}
              />
              {user.full_name && (
                <Text
                  $weight={typography.fontWeights.MEDIUM}
                  style={{ marginTop: 12 }}
                >
                  {user.full_name}
                </Text>
              )}
              {user.email && (
                <Text type="secondary" style={{ marginTop: 4 }}>
                  {user.email}
                </Text>
              )}
              {!user.is_onboarding && (
                <WorkspaceSwitch style={{ marginTop: 16 }} />
              )}
            </>
          )}
          {showUpgradeButton && (
            <UpgradeButton style={{ alignSelf: "center", marginTop: 16 }} />
          )}
          <WorkspaceWrapper style={{ padding: "16px 0 0 0" }}>
            {showWorkspaceSelector && (
              <WorkspaceSelector workspace={workspace} />
            )}
            <UserInfoItems>
              {userInfoItems.map((item) => {
                return (
                  <Flex key={item.key}>
                    <Text>{item.key}</Text>
                    <Text type={item.type}>{item.value}</Text>
                  </Flex>
                );
              })}
            </UserInfoItems>
          </WorkspaceWrapper>
          {(!user?.is_onboarding || isAdminWorkspace) && (
            <MenuDivider style={{ marginTop: 16 }} />
          )}
          {!user?.is_onboarding && (
            <>
              <Flex
                style={{
                  flexDirection: "column",
                  rowGap: 12,
                  width: "100%",
                  padding: "12px 0",
                }}
              >
                <CurrencyToggle style={{ width: "100%" }} />
                <DarkModeToggle />
              </Flex>
              <MenuDivider />
            </>
          )}
        </MobileTopSectionWrapper>
      );
    }
    return (
      <>
        <LogoWrapper
          onClick={() => history.push(getHomePath())}
          style={{ marginBottom: collapsed ? 20 : 0 }}
        >
          <Logo src={isDarkMode ? cornixWhiteLogo : cornixLogo} />
        </LogoWrapper>
        {showUpgradeButton && (
          <UpgradeButton style={{ alignSelf: "center", marginBottom: 24 }} />
        )}
        {!useDrawerMenu && !defaultCollapse && (
          <CollapseButton
            onClick={() => handleSetIsCollapsed(!collapsed)}
            $collapsed={collapsed}
          >
            <CaretRightOutlined style={{ marginLeft: 2, fontSize: 14 }} />
          </CollapseButton>
        )}
        <WorkspaceWrapper style={{ marginTop: collapsed ? 0 : 24 }}>
          {showWorkspaceSelector && <WorkspaceSelector workspace={workspace} />}
          {showGroupSelect && (
            <GroupSelect style={{ marginBottom: 16, width: "100%" }} />
          )}
          {isLargeMobile && !user?.is_onboarding && (
            <WorkspaceSwitch style={{ marginBottom: 16 }} />
          )}
          {(useDrawerMenu || !collapsed) && !user?.is_onboarding && (
            <MenuDivider />
          )}
        </WorkspaceWrapper>
      </>
    );
  };

  const menu = (
    <StyledMenu
      defaultSelectedKeys={["/"]}
      selectedKeys={selectedKeys}
      defaultOpenKeys={defaultOpenKeys}
      mode="inline"
      $collapsed={collapsed && !mobileMenuOpen}
      $isBottomNavigator={bottomNavigatorData.shouldDisplay}
      items={renderMenu()}
      style={{
        marginTop:
          bottomNavigatorData.baseShouldDisplay || user?.is_onboarding
            ? 0
            : undefined,
      }}
    />
  );

  const adminBanner =
    (!collapsed || useDrawerMenu) && workspace === WorkspaceType.ADMIN ? (
      <AdminCommunityBanner />
    ) : undefined;
  const socialMedia = (!collapsed || useDrawerMenu) && (
    <SocialMediaWrapper id="app-social-resources">
      <SocialMedia />
    </SocialMediaWrapper>
  );
  const qrPopover = collapsed && !mobileMenuOpen && isMenuAnimatingClose && (
    <QrPopover isAppStore={isIOS} isCollapsed={true} />
  );

  const getContent = () => {
    if (bottomNavigatorData.baseShouldDisplay || userIsLimitedAccess) {
      return (
        <>
          <MobileScrollContent>
            {getSiderTopSection()}
            {menu}
            {!IS_TELEGRAM_WEB_APP && (
              <>
                <MenuDivider style={{ margin: "8px 0" }} />
                <LogoutButton onClick={logout}>
                  <LogoutOutlined style={{ marginRight: 26, fontSize: 16 }} />
                  Logout
                </LogoutButton>
              </>
            )}
            {adminBanner}
          </MobileScrollContent>
          {socialMedia}
          {qrPopover}
        </>
      );
    }
    return (
      <>
        {getSiderTopSection()}
        {menu}
        {adminBanner}
        {socialMedia}
        {qrPopover}
      </>
    );
  };

  return (
    <MenuWrapper
      {...(useDrawerMenu ? menuProps.drawer : menuProps.sider)}
      style={{
        borderRight: appTheme.borders.lightest,
        background: hexToRGB(appTheme.colors.bgColor, 0.4),
      }}
      theme={"light" as any}
    >
      <InnerWrapper
        $safeAreaInset={safeAreaInset}
        $contentSafeAreaInset={contentSafeAreaInset}
      >
        {getContent()}
      </InnerWrapper>
    </MenuWrapper>
  );
};

export default Sider;

const COLLAPSED_MENU_KEY = "collapsed_menu";

const StyledDrawer = styled(Drawer)`
  &&& {
    .ant-drawer-body {
      padding-bottom: 0 !important;
    }
  }
`;
const StyledMenuWrapper = styled(Layout.Sider)`
  .ant-layout-sider-children {
    height: 100%;
  }
`;
const InnerWrapper = styled.div<{
  $safeAreaInset?: Inset;
  $contentSafeAreaInset?: Inset;
}>`
  height: 100%;
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.colors.bgColor};
  padding-top: ${(p) =>
    (p.$safeAreaInset?.top ?? 0) + (p.$contentSafeAreaInset?.top ?? 0)}px;
`;
const MobileScrollContent = styled.div`
  overflow-y: auto;
  flex: 1;
`;
const LogoWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 16px 0 20px 0;
  flex: none;
  cursor: pointer;
`;
const Logo = styled.img`
  width: 85%;
  max-height: 100px;
`;
const SocialMediaWrapper = styled.div`
  width: 100%;
  flex: none;
`;
const MenuDivider = styled(Divider)`
  margin: 0;
  border-top: ${(p) => `${p.theme.borders.lightest}`};
`;
const CollapseButton = styled(Button).attrs({
  shape: "circle",
  type: "primary",
  size: "small",
})<{ $collapsed: boolean }>`
  position: absolute;
  left: 100%;
  top: ${HEADER_HEIGHT}px;
  transform: translate(-50%, -50%)
    ${(p) => (!p.$collapsed ? "rotate(180deg)" : "")};
  color: ${(p) => p.theme.colors.basic};
  z-index: ${zIndexes.drawer};
`;
const StyledMenu = styled(Menu)<{
  $collapsed: boolean;
  $isBottomNavigator?: boolean;
}>`
  ${(p) =>
    !p.$isBottomNavigator &&
    `
    overflow-y: auto;
    overflow-x: hidden;
    flex: auto;
    `};
  margin-top: 24px;
  ${(p) =>
    p.$collapsed &&
    `
    width: 50px;
  `};

  ul::-webkit-scrollbar,
  div::-webkit-scrollbar,
  &::-webkit-scrollbar {
    width: ${(p) => (p.$collapsed ? 6 : 10)}px !important;
  }
`;
const IconWrapper = styled.div`
  transform: translate(0, -4px);
`;
const WorkspaceWrapper = styled.div`
  padding: 0 16px;
  width: 100%;
`;
const MobileTopSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 8px 16px;
`;
const UserInfoItems = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;
const LogoutButton = styled.div`
  padding: 0 16px 0 24px;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${(p) => p.theme.colors.textColor};
  margin-bottom: 8px;
`;

enum AppScreen {
  DASHBOARD = "dashboard",
  TRADES = "trades",
  MARKETPLACE = "marketplace",
  ACCOUNTS = "accounts",
  ASSETS = "assets",
  GROUP_MANAGEMENT = "groupManagement",
  ADMIN_SIGNALS = "adminSignals",
  ADMIN_TRADING_CONFIGURATION = "adminTradingConfiguration",
  AFFILIATION = "affiliation",
  BILLING = "billing",
  CANCEL_SUBSCRIPTION = "cancelSubscription",
}

const IGNORE_BOTTOM_NAVIGATOR_KEYS = new Set([
  AppScreen.DASHBOARD,
  AppScreen.TRADES,
  AppScreen.MARKETPLACE,
  AppScreen.ACCOUNTS,
  AppScreen.ASSETS,
  AppScreen.AFFILIATION,
  AppScreen.GROUP_MANAGEMENT,
  AppScreen.ADMIN_SIGNALS,
  AppScreen.ADMIN_TRADING_CONFIGURATION,
  ...Object.values(BotType).map((type) => type.toString()),
]);
const BILLING_MENU_ITEM = {
  key: AppScreen.BILLING,
  name: "Billing",
  link: BASE_BILLING_SETTINGS_PATH,
  routerMatch: BASE_BILLING_SETTINGS_PATH,
  icon: <BillingIcon />,
  group: SiderGroup.OTHER,
};
const ACCOUNT_AND_PRIVACY_SUB_MENU_ITEM = {
  key: "accountsAndSecurity",
  name: "Account & Security",
  link: SECURITY_SETTINGS_PATH,
  routerMatch: SECURITY_SETTINGS_PATH,
};
const NOTIFICATIONS_SUB_MENU = {
  key: "notifications",
  name: "Notifications Management",
  link: NOTIFICATIONS_SETTINGS_PATH,
  routerMatch: NOTIFICATIONS_SETTINGS_PATH,
};
const SETTINGS_MENU_ITEM = {
  key: "settings",
  name: "Settings",
  icon: <SettingOutlined />,
  routerMatch: SETTINGS_PATH,
  link: SECURITY_SETTINGS_PATH,
  subMenu: [ACCOUNT_AND_PRIVACY_SUB_MENU_ITEM, NOTIFICATIONS_SUB_MENU],
  group: SiderGroup.OTHER,
};
export const DASHBOARD_MENU_ITEM = {
  key: AppScreen.DASHBOARD,
  name: "Dashboard",
  icon: <DashboardIcon />,
  link: DASHBOARD_PATH,
  routerMatch: DASHBOARD_PATH,
};
export const ADMIN_GROUP_MANAGEMENT_MENU_ITEM = {
  key: AppScreen.GROUP_MANAGEMENT,
  icon: <GroupManagementIcon />,
  link: getGroupManagementRoute(),
  name: "Users Management",
  routerMatch: GROUP_MANAGEMENT_PATH,
};
export const ADMIN_SIGNALS_MENU_ITEM = {
  key: AppScreen.ADMIN_SIGNALS,
  icon: <SignalsIcon />,
  name: "Signals",
  link: ADMIN_SIGNALS_PATH,
  routerMatch: ADMIN_SIGNALS_PATH,
};
const largeIconStyles = css`
  &&&& {
    font-size: 18px !important;
    margin-right: -2px;
    margin-left: -1px;
  }
`;
const StyledAccountsIcon = styled(AccountsIcon)`
  ${largeIconStyles};
`;
const StyledGroupsIcon = styled(GroupsIcon)`
  ${largeIconStyles};
`;
export const ACCOUNTS_MENU_ITEM = {
  icon: <StyledAccountsIcon />,
  key: AppScreen.ACCOUNTS,
  name: "Accounts",
  link: PORTFOLIO_ACCOUNTS_PATH,
  routerMatch: PORTFOLIO_ACCOUNTS_PATH,
  group: SiderGroup.PORTFOLIO,
};
export const ASSETS_MENU_ITEM = {
  icon: <PieChartOutlined />,
  key: AppScreen.ASSETS,
  name: "Assets",
  link: PORTFOLIO_ASSETS_PATH,
  routerMatch: PORTFOLIO_ASSETS_PATH,
  group: SiderGroup.PORTFOLIO,
};
export const TRADES_MENU_ITEM = {
  icon: <SwapOutlined />,
  name: "Trades",
  key: AppScreen.TRADES,
  routerMatch: BASE_TRADES_PATH,
  link: OPEN_TRADES_PATH,
};
const TRADING_TERMINAL_MENU_ITEM = {
  icon: <FundProjectionScreenOutlined />,
  name: "Trading Terminal",
  key: "tradingTerminal",
  routerMatch: CREATE_TRADE_PATH,
  link: CREATE_TRADE_PATH,
  group: SiderGroup.TRADING,
};
const SIGNALS_MENU_ITEM = {
  icon: <SignalsIcon />,
  name: "Signals",
  link: USER_SIGNALS_PATH,
  key: "signals",
  routerMatch: USER_SIGNALS_PATH,
  group: SiderGroup.MONITORING,
};
const WATCHLIST_MENU_ITEM = {
  key: "watchlist",
  icon: <EyeOutlined />,
  name: "Watchlist & Markets",
  link: WATCHLIST_PATH,
  routerMatch: WATCHLIST_PATH,
  group: SiderGroup.MONITORING,
};
export const MARKETPLACE_MENU_ITEM = {
  key: AppScreen.MARKETPLACE,
  icon: <ShopOutlined />,
  name: "Marketplace",
  link: MARKETPLACE_PATH,
  routerMatch: MARKETPLACE_PATH,
  group: SiderGroup.TRADING,
};
const MY_GROUPS_MENU_ITEM = {
  key: `my-groups`,
  icon: <StyledGroupsIcon />,
  name: "My Groups",
  link: MY_GROUPS_PATH,
  routerMatch: MY_GROUPS_PATH,
  group: SiderGroup.OTHER,
};
const getBotsSubMenu = (botType: BotType) => {
  if (![BotType.SIGNAL, BotType.ASAP].includes(botType)) return;
  const myBotsLink = BOT_TYPE_TO_BASE_MY_BOTS_PATH[botType];
  const baseMyBotsLink = BOT_TYPE_TO_BASE_PATH[botType];
  const backtestingLink = BOT_TYPE_TO_BASE_BACKTESTING_PATH[botType];
  const subMenuItems: MenuItemConfig[] = [
    {
      key: `my-${botType}-bots`,
      name: "My Bots",
      link: myBotsLink,
      routerMatch: [myBotsLink, baseMyBotsLink],
      exactMatch: true,
    },
  ];
  if (botType === BotType.ASAP) {
    subMenuItems.push({
      key: `public-${botType}-bots`,
      name: "Public Bots",
      link: DCA_PUBLIC_BOTS_PATH,
      routerMatch: DCA_PUBLIC_BOTS_PATH,
    });
  }
  subMenuItems.push({
    key: `${botType}-bots-backtesting`,
    name: "Backtesting",
    link: backtestingLink,
    routerMatch: backtestingLink,
  });
  return subMenuItems;
};
export const getBotsMenuItems = (
  isBottomNavigator?: boolean,
  isDarkMode?: boolean
) =>
  BOT_TYPES.map((botType) => {
    const botTypeToIcon = isBottomNavigator
      ? isDarkMode
        ? DARK_BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON
        : BOTTOM_NAVIGATOR_BOT_TYPE_TO_ICON
      : BOT_TYPE_TO_ICON;
    const Icon = botTypeToIcon[botType];
    const iconSize = isBottomNavigator ? 30 : 16;
    const isSignalsBot = botType === BotType.SIGNAL;
    const link = BOT_TYPE_TO_BASE_PATH[botType];

    return {
      key: botType.toString(),
      icon: <Icon style={{ width: iconSize, height: iconSize }} />,
      name: `${BOT_TYPE_TO_DISPLAY_NAME[botType]} Bots`,
      link,
      routerMatch: link,
      group: SiderGroup.TRADING,
      subName: isSignalsBot ? "(Auto-Trading)" : undefined,
      className: isSignalsBot ? "with-sub-name" : undefined,
      subMenu: getBotsSubMenu(botType),
    };
  });

const BOTS_MENU_ITEMS = getBotsMenuItems();

export const AFFILIATION_MENU_ITEM = {
  key: AppScreen.AFFILIATION,
  icon: <CrownOutlined />,
  name: "Affiliation",
  link: AFFILIATION_PATH,
  routerMatch: AFFILIATION_PATH,
  group: SiderGroup.OTHER,
};
export const ADMIN_TRADING_CONFIGURATION_MENU_ITEM = {
  key: AppScreen.ADMIN_TRADING_CONFIGURATION,
  icon: <Sliders />,
  name: "Trading Configuration",
  link: ADMIN_TRADING_CONFIGURATION_PATH,
  routerMatch: ADMIN_TRADING_CONFIGURATION_PATH,
};

const BASE_USER_WORKSPACE_ITEMS = [
  DASHBOARD_MENU_ITEM,
  TRADES_MENU_ITEM,
  ACCOUNTS_MENU_ITEM,
  ASSETS_MENU_ITEM,
  ...BOTS_MENU_ITEMS,
  TRADING_TERMINAL_MENU_ITEM,
  SIGNALS_MENU_ITEM,
  WATCHLIST_MENU_ITEM,
  MY_GROUPS_MENU_ITEM,
  MARKETPLACE_MENU_ITEM,
  AFFILIATION_MENU_ITEM,
  BILLING_MENU_ITEM,
  SETTINGS_MENU_ITEM,
];
export const workspaceToMenuItems: {
  [workspaceKey in WorkspaceType]: MenuItemConfig[];
} = {
  [WorkspaceType.ADMIN]: [
    ADMIN_GROUP_MANAGEMENT_MENU_ITEM,
    ADMIN_TRADING_CONFIGURATION_MENU_ITEM,
    ADMIN_SIGNALS_MENU_ITEM,
    AFFILIATION_MENU_ITEM,
    SETTINGS_MENU_ITEM,
  ],
  [WorkspaceType.USER]: BASE_USER_WORKSPACE_ITEMS,
  [WorkspaceType.DEMO]: BASE_USER_WORKSPACE_ITEMS,
};

export const workspaceToPaths: WorkspaceToPathsMapping = getWorkspaceToPath();
